'use client';

import { getThemeBackgroundClass, getThemeTextClass } from '@/lib/theme';
import { ITheme } from '@/types/theme';
import React from 'react';
import CtaButtons from './CtaButtons';
import WaveDivider from './WaveDivider';

interface TryBookingSystemProps {
  lang?: string;
  backgroundColor?: 'primary' | 'secondary';
  theme?: ITheme;
}

const TryBookingSystem: React.FC<TryBookingSystemProps> = ({
  lang = 'en',
  backgroundColor = 'secondary',
  theme = null,
}) => {
  const content = {
    en: {
      title: 'Try the Booking System',
      highlight: 'for 7 days',
      subtitle: 'All Features Included',
      features: [
        { icon: '∞', text: 'Unlimited bookings', bold: 'Unlimited' },
        { icon: '✨', text: 'All AI features included', bold: 'AI features' },
        {
          icon: '🌐',
          text: 'Your own branded booking website',
          bold: 'branded booking website',
        },
      ],
    },
    zh: {
      title: '試用預訂系統',
      highlight: '7天',
      subtitle: '包含所有功能',
      features: [
        { icon: '∞', text: '無限預訂次數', bold: '無限' },
        { icon: '✨', text: '所有 AI 功能', bold: 'AI 功能' },
        { icon: '🌐', text: '專屬品牌預訂網站', bold: '品牌預訂網站' },
      ],
    },
  };

  const t = content[lang as keyof typeof content] || content.en;

  const bgClasses = getThemeBackgroundClass(theme, backgroundColor);
  const themeColor = theme ? getThemeTextClass(theme) : 'text-[#3880ff]';

  return (
    <section className={`${bgClasses} py-16 md:py-24 relative`}>
      {/* Wave at top */}
      <WaveDivider
        position="top"
        backgroundColor={backgroundColor}
        theme={theme}
      />
      <div
        className="container mx-auto px-4 max-w-7xl"
        style={{ marginTop: '50px' }}
      >
        <header className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3">
            {t.title} <span className={themeColor}>{t.highlight}</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            {t.subtitle}
          </p>
        </header>

        <article className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16">
          {/* Illustration */}
          <div className="flex-shrink-0 w-full lg:w-auto max-w-md">
            <div className="relative">
              <svg
                width="400"
                height="320"
                viewBox="0 0 400 320"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-auto"
              >
                {/* Background gradient circle */}
                <circle
                  cx="200"
                  cy="160"
                  r="130"
                  fill="#e0f2fe"
                  opacity="0.5"
                />
                <circle
                  cx="200"
                  cy="160"
                  r="100"
                  fill="#bae6fd"
                  opacity="0.3"
                />

                {/* Central Calendar - main element */}
                <g transform="translate(140, 95)">
                  <rect
                    width="120"
                    height="110"
                    rx="12"
                    fill="white"
                    stroke="#e2e8f0"
                    strokeWidth="2"
                  />
                  {/* Calendar header */}
                  <rect width="120" height="28" rx="12" fill="#3880ff" />
                  <text
                    x="60"
                    y="19"
                    textAnchor="middle"
                    fill="white"
                    fontSize="12"
                    fontWeight="bold"
                  >
                    7 DAYS FREE
                  </text>
                  {/* Calendar grid */}
                  <circle cx="25" cy="50" r="8" fill="#10b981" />
                  <circle cx="60" cy="50" r="8" fill="#3880ff" />
                  <circle cx="95" cy="50" r="8" fill="#e2e8f0" />
                  <circle cx="25" cy="75" r="8" fill="#e2e8f0" />
                  <circle cx="60" cy="75" r="8" fill="#10b981" />
                  <circle cx="95" cy="75" r="8" fill="#3880ff" />
                  <circle cx="25" cy="95" r="6" fill="#e2e8f0" />
                  <circle cx="60" cy="95" r="6" fill="#e2e8f0" />
                  <circle cx="95" cy="95" r="6" fill="#10b981" />
                  {/* Checkmark on one */}
                  <path
                    d="M22 50 L25 53 L30 46"
                    stroke="white"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                  />
                </g>

                {/* AI Brain Icon - top right */}
                <g transform="translate(290, 60)">
                  <circle
                    cx="30"
                    cy="30"
                    r="28"
                    fill="#f0fdf4"
                    stroke="#10b981"
                    strokeWidth="2"
                  />
                  <path
                    d="M20 30 Q20 20 30 20 Q40 20 40 30 Q40 40 30 40 Q20 40 20 30"
                    fill="#10b981"
                    opacity="0.3"
                  />
                  <circle cx="25" cy="26" r="4" fill="#10b981" />
                  <circle cx="35" cy="26" r="4" fill="#10b981" />
                  <circle cx="30" cy="35" r="3" fill="#10b981" />
                  <path
                    d="M25 26 L30 35 L35 26"
                    stroke="#10b981"
                    strokeWidth="1.5"
                    fill="none"
                  />
                  {/* Sparkles around AI */}
                  <path
                    d="M50 15 L52 20 L57 18 L53 23 L50 28 L47 23 L43 25 L47 20 Z"
                    fill="#fbbf24"
                  />
                </g>

                {/* Money/Coins Icon - bottom right */}
                <g transform="translate(300, 180)">
                  <circle
                    cx="25"
                    cy="25"
                    r="24"
                    fill="#fef3c7"
                    stroke="#f59e0b"
                    strokeWidth="2"
                  />
                  <text
                    x="25"
                    y="32"
                    textAnchor="middle"
                    fill="#f59e0b"
                    fontSize="24"
                    fontWeight="bold"
                  >
                    $
                  </text>
                  {/* Coin stack effect */}
                  <ellipse
                    cx="25"
                    cy="45"
                    rx="18"
                    ry="5"
                    fill="#fbbf24"
                    opacity="0.5"
                  />
                </g>

                {/* Report/Chart Icon - bottom left */}
                <g transform="translate(40, 190)">
                  <rect
                    width="70"
                    height="55"
                    rx="8"
                    fill="white"
                    stroke="#e2e8f0"
                    strokeWidth="2"
                  />
                  {/* Chart bars */}
                  <rect
                    x="12"
                    y="35"
                    width="10"
                    height="12"
                    rx="2"
                    fill="#3880ff"
                  />
                  <rect
                    x="26"
                    y="25"
                    width="10"
                    height="22"
                    rx="2"
                    fill="#10b981"
                  />
                  <rect
                    x="40"
                    y="18"
                    width="10"
                    height="29"
                    rx="2"
                    fill="#3880ff"
                  />
                  <rect
                    x="54"
                    y="28"
                    width="10"
                    height="19"
                    rx="2"
                    fill="#f59e0b"
                  />
                  {/* Trend line */}
                  <path
                    d="M12 30 L22 28 L36 18 L50 22 L64 15"
                    stroke="#10b981"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                  />
                  <circle cx="64" cy="15" r="3" fill="#10b981" />
                </g>

                {/* User/Booking Icon - top left */}
                <g transform="translate(50, 55)">
                  <circle
                    cx="28"
                    cy="28"
                    r="26"
                    fill="#ede9fe"
                    stroke="#8b5cf6"
                    strokeWidth="2"
                  />
                  <circle cx="28" cy="22" r="10" fill="#8b5cf6" />
                  <path d="M12 45 Q12 35 28 35 Q44 35 44 45" fill="#8b5cf6" />
                  {/* Plus badge */}
                  <circle cx="42" cy="42" r="10" fill="#10b981" />
                  <path
                    d="M42 38 L42 46 M38 42 L46 42"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </g>

                {/* Clock/Schedule Icon - left middle */}
                <g transform="translate(35, 125)">
                  <circle
                    cx="22"
                    cy="22"
                    r="20"
                    fill="#fef2f2"
                    stroke="#ef4444"
                    strokeWidth="2"
                  />
                  <circle cx="22" cy="22" r="3" fill="#ef4444" />
                  <path
                    d="M22 22 L22 12"
                    stroke="#ef4444"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M22 22 L30 26"
                    stroke="#ef4444"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </g>

                {/* Website/Globe Icon - right middle */}
                <g transform="translate(320, 120)">
                  <circle
                    cx="22"
                    cy="22"
                    r="20"
                    fill="#ecfeff"
                    stroke="#06b6d4"
                    strokeWidth="2"
                  />
                  <ellipse
                    cx="22"
                    cy="22"
                    rx="8"
                    ry="18"
                    fill="none"
                    stroke="#06b6d4"
                    strokeWidth="1.5"
                  />
                  <path d="M4 22 L40 22" stroke="#06b6d4" strokeWidth="1.5" />
                  <path d="M8 14 L36 14" stroke="#06b6d4" strokeWidth="1" />
                  <path d="M8 30 L36 30" stroke="#06b6d4" strokeWidth="1" />
                </g>

                {/* Decorative elements */}
                {/* Floating dots */}
                <circle cx="180" cy="50" r="4" fill="#3880ff" opacity="0.6" />
                <circle cx="220" cy="270" r="5" fill="#10b981" opacity="0.6" />
                <circle cx="100" cy="270" r="3" fill="#f59e0b" opacity="0.6" />
                <circle cx="320" cy="260" r="4" fill="#8b5cf6" opacity="0.6" />

                {/* Connection lines (subtle) */}
                <path
                  d="M78 83 Q120 100 140 115"
                  stroke="#e2e8f0"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  fill="none"
                />
                <path
                  d="M260 125 Q280 100 290 88"
                  stroke="#e2e8f0"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  fill="none"
                />
                <path
                  d="M260 175 Q290 180 300 192"
                  stroke="#e2e8f0"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  fill="none"
                />
                <path
                  d="M140 195 Q115 195 110 202"
                  stroke="#e2e8f0"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  fill="none"
                />

                {/* Sparkle stars */}
                <path
                  d="M130 55 L132 62 L139 60 L134 66 L130 73 L126 66 L121 68 L126 62 Z"
                  fill="#fbbf24"
                />
                <path
                  d="M280 250 L282 255 L287 254 L283 258 L280 263 L277 258 L273 259 L277 255 Z"
                  fill="#fbbf24"
                />
              </svg>
            </div>
          </div>

          {/* Content */}
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
            <ul className="space-y-4 mb-8">
              {t.features.map((feature, index) => (
                <li
                  key={index}
                  className="flex items-center gap-3 text-lg text-gray-700 dark:text-gray-300"
                >
                  <span className="text-2xl flex-shrink-0">{feature.icon}</span>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: feature.text.replace(
                        new RegExp(`(${feature.bold})`, 'gi'),
                        '<strong class="font-bold text-gray-900 dark:text-white">$1</strong>'
                      ),
                    }}
                  />
                </li>
              ))}
            </ul>

            <CtaButtons
              theme={theme}
              includeDemo={false}
              language={lang as 'en' | 'zh'}
              location="try_booking_system"
            />
          </div>
        </article>
      </div>
    </section>
  );
};

export default TryBookingSystem;
